/**
 * How a verdict is worded and coloured — kept in ONE place because this is where
 * the product either does or does not cause a wrongful citation.
 *
 * The server already refuses to assert a negative on a lot it cannot see
 * completely (it sends `unverified` instead of `expired`). This module's job is
 * to not undo that in the UI: nothing here ever renders "unauthorized", because
 * we are never in a position to know that. What we know is whether WE hold an
 * authorization. A car with no record might have paid on another rail, might be
 * inside the lot's arrival grace, might be a resident with an arrangement nobody
 * told us about. So the negative wording tops out at "no record" plus what to do
 * about it, and the decision to cite stays with the officer.
 *
 * This is also why "Expired" and "No record" are different words. FlowPark's
 * equivalent screen labels its negative bucket "Unauthorized", which reads as a
 * cite-list and quietly implies the roster is complete. It cannot be — a car that
 * never paid leaves no row to list.
 */
import type { Verdict, Vehicle } from './api'

/**
 * `ok` green, `warn` amber, `bad` red, `unknown` grey — and `info` blue.
 *
 * `info` is the fifth, added for zone closures, and it is a genuinely new
 * CATEGORY rather than a fifth shade. The other four all answer "how bad is this
 * car?" on one axis from fine to citable. A closed lot is not a point on that
 * axis at all: it says the question does not apply here. Blue because the four
 * existing colours are the officer's severity vocabulary and reusing any of them
 * would put a closure somewhere on a scale it does not belong on — grey
 * (`unknown`) in particular reads as "we couldn't tell", which is the opposite of
 * what a closure is.
 *
 * ⚠️ Both `info` AND `unknown` need CSS in every family they appear in. `.result--`
 * had no `unknown` rule before this change even though `toneFor('unverified')`
 * has returned it since hybrid coverage shipped, so the `unverified` result card
 * has been rendering on the bare `.result` background all along. Adding a tone
 * without its rule is silent — nothing errors, the card just loses its colour.
 */
export type Tone = 'ok' | 'warn' | 'bad' | 'unknown' | 'info'

/**
 * Minutes → something an officer can act on at a glance.
 *
 * "301 min ago" is arithmetic, not information. Minutes stay minutes up to 90
 * (where they're the unit that matters — is this 4 minutes over or 40?), and
 * anything longer rounds to hours, because past an hour the exact figure stops
 * changing the decision.
 */
export function formatMinutes(mins: number | null): string {
  if (mins == null) return ''
  if (mins < 90) return `${mins} min`
  const h = Math.floor(mins / 60)
  const m = mins % 60
  if (h >= 24) {
    const d = Math.floor(h / 24)
    return `${d} ${d === 1 ? 'day' : 'days'}`
  }
  return m ? `${h}h ${m}m` : `${h} ${h === 1 ? 'hour' : 'hours'}`
}

/**
 * The compact form a ROW uses, the demo's `hm()`: "37m", "1h 13m", "2h". A row is
 * one narrow line beside a pill, so it drops the spelled-out unit that
 * `formatMinutes` keeps for the card ("75 min", "2 hours"). Past a day it still
 * says days. Display only: the card and the spoken row name keep the long form.
 */
export function rowDuration(mins: number | null): string {
  if (mins == null) return ''
  if (mins < 60) return `${mins}m`
  const h = Math.floor(mins / 60)
  const m = mins % 60
  if (h >= 24) {
    const d = Math.floor(h / 24)
    return `${d} ${d === 1 ? 'day' : 'days'}`
  }
  return m ? `${h}h ${m}m` : `${h}h`
}

/**
 * How a plate's jurisdiction should be marked, if at all.
 *
 * A plate is unique only within the jurisdiction that issued it, so on this
 * screen the state is part of the car's identity. But the checkout selector is
 * PREFILLED from the zone, which means a stored "GA" is either something the
 * driver asserted or something we guessed — and an officer deciding whether to
 * boot a car needs to know which.
 *
 * Three marks, matching the three things that are actually true:
 *
 *   STATED   — a human asserted it about this car (driver at checkout, or an
 *              operator typing a permit roster).
 *   ASSUMED  — inferred from the lot's own jurisdiction. Right for most cars in
 *              that lot and wrong for exactly the out-of-state ones this exists
 *              to catch, so it is labelled rather than hidden or promoted.
 *   NO STATE — nothing recorded. A fourth state, NOT a synonym for ASSUMED.
 *
 * ⚠️ IT REPORTS, IT DOES NOT INSTRUCT. None of these three carries guidance
 * about whether to cite. Patrol says what it knows and the partner applies their
 * own policy — the same rule that stops the negative verdicts wording themselves
 * as "unauthorized". An ASSUMED badge that said "verify before citing" would be
 * this surface inventing an enforcement policy nobody set.
 */
export type PlateStateMark = 'stated' | 'assumed' | 'none'

export function plateStateMarkFor(v: Vehicle): PlateStateMark | null {
  // No jurisdiction recorded at all. Distinct from an inferred one, and worth
  // showing: "we do not know where this plate is from" is a real fact about the
  // identity of the car in front of the officer.
  if (!v.plateState) return 'none'
  // A state with no source is unknown provenance, and unknown is not confirmed.
  // Falls to ASSUMED rather than STATED so the uncertain direction is the
  // default — claiming a driver asserted something they may not have is the
  // error that matters here.
  if (v.plateStateSource === 'driver' || v.plateStateSource === 'operator') return 'stated'
  return 'assumed'
}

/** The badge's word. Short, because it sits beside the plate at arm's length. */
export function plateStateMarkLabel(mark: PlateStateMark): string {
  return mark === 'stated' ? 'STATED' : mark === 'assumed' ? 'ASSUMED' : 'NO STATE'
}

/**
 * The mark as a screen reader should hear it: "GA stated", "GA assumed", "no
 * state". The visible badge is "GA" + "STATED" in two spans; a button that is
 * given an aria-label does not expose its content, so the row's name has to
 * carry this itself. Reports provenance only, like the label does.
 */
export function plateStateMarkSpoken(v: Vehicle): string {
  const mark = plateStateMarkFor(v)
  if (!mark) return ''
  if (mark === 'none') return 'no state'
  const word = plateStateMarkLabel(mark).toLowerCase()
  return v.plateState ? `${v.plateState} ${word}` : word
}

/** The hover/aria explanation. States provenance; never what to do about it. */
export function plateStateMarkTitle(mark: PlateStateMark): string {
  switch (mark) {
    case 'stated':
      return 'The driver or an operator recorded this issuing state for this plate.'
    case 'assumed':
      return 'Not recorded by the driver or an operator: prefilled from the lot, or with no source on record. Treat as inferred.'
    case 'none':
      return 'No issuing state on record for this plate.'
  }
}

/**
 * Which characters of a candidate plate differ from what the officer typed.
 *
 * The server already says WHY a candidate is offered ("0 read as O"), and that
 * sentence is what names the confusion. This says WHERE: it returns one boolean
 * per character of the candidate, so the row can mark the positions that differ
 * and send the officer's eye straight at them. An officer comparing MNP2210
 * against MNP2216 at arm's length, outdoors, one-handed, should not have to
 * scan seven characters to find the one that moved.
 *
 * Purely presentational. It never decides anything: the candidate list, its
 * order and its reason all come from the server, and a wrong mask makes a row
 * uglier, not more dangerous.
 *
 * Unequal lengths are the case worth stating. A missing or extra character
 * shifts every position after it, so a naive index-by-index compare would mark
 * the whole tail as different and point at nothing. This tries each possible
 * gap position and keeps the alignment with the fewest mismatches, which is what
 * makes "one character missing" render as one mark rather than five.
 */
// Look-alike classes, from the demo's CONFUSABLE_PAIRS (connected, so 0/O/Q/D is one class).
const CONFUSABLE_CLASSES = ['0OQD', '1IL', '5S', '8B', '2Z', '6G', 'UV']
const CLASS_OF: Record<string, string> = {}
for (const cls of CONFUSABLE_CLASSES) for (const ch of cls) CLASS_OF[ch] = cls[0]

const plateSkeleton = (v: string) =>
  (v || '').toUpperCase().replace(/[^A-Z0-9]/g, '').split('').map(c => CLASS_OF[c] ?? c).join('')

/**
 * Where the typed fragment sits inside a plate, collapsing look-alike characters
 * first so typing the letter O finds the plate stamped with a zero. Null when it
 * is not in the plate. Narrowing the roster is lower-stakes than authorizing:
 * one row too many costs nothing, a hidden row could cost a wrongful boot, so
 * this only ever widens what plain substring matching would show.
 */
export function rosterMatch(query: string, plate: string): { at: number; len: number } | null {
  const q = plateSkeleton(query)
  if (!q) return { at: -1, len: 0 }
  const at = plateSkeleton(plate).indexOf(q)
  return at < 0 ? null : { at, len: q.length }
}

export function plateDiffMask(typed: string, candidate: string): boolean[] {
  const q = (typed || '').toUpperCase()
  const c = (candidate || '').toUpperCase()
  if (!q) return c.split('').map(() => false)
  if (q.length === c.length) return c.split('').map((ch, i) => ch !== q[i])

  const candidateIsLonger = c.length > q.length
  const longer = candidateIsLonger ? c : q
  const shorter = candidateIsLonger ? q : c

  let best: boolean[] | null = null
  let bestScore = Number.POSITIVE_INFINITY
  for (let gap = 0; gap < longer.length; gap++) {
    const mask: boolean[] = []
    let score = 0
    let si = 0
    for (let li = 0; li < longer.length; li++) {
      if (li === gap) {
        // The unpaired character itself. Half a point so that, all else equal,
        // an alignment prefers to spend its gap where the strings genuinely
        // diverge rather than at position 0 by default.
        mask.push(true)
        score += 0.5
        continue
      }
      const differs = longer[li] !== shorter[si]
      mask.push(differs)
      if (differs) score += 1
      si++
    }
    if (score < bestScore) { bestScore = score; best = mask }
  }
  if (!best) return c.split('').map(() => false)
  // The mask is indexed over the LONGER string. When the candidate is the longer
  // one it already lines up; otherwise drop the gap position to get back to the
  // candidate's own indices.
  if (candidateIsLonger) return best
  const gap = best.indexOf(true)
  return best.filter((_, i) => i !== gap).slice(0, c.length)
}

export function toneFor(v: Verdict): Tone {
  switch (v) {
    case 'authorized': return 'ok'
    // Grace is internal on the roster. The officer's action is identical to an
    // authorization we hold — leave the car alone — so colour must not disclose
    // the countdown that the words deliberately hide.
    case 'grace': return 'ok'
    case 'pending': return 'warn'
    case 'expired': return 'bad'
    case 'unverified': return 'unknown'
    case 'no_record': return 'bad'
    case 'closed': return 'info'
  }
}

/** The single-plate answer may say grace is not enforceable, but never as an
 * amber "almost" state. Amber invites the officer to wait at the car. */
export function resultToneFor(v: Verdict): Tone {
  return v === 'grace' ? 'unknown' : toneFor(v)
}

/** Short label for a list row. */
export function labelFor(v: Vehicle): string {
  switch (v.verdict) {
    case 'authorized':
      return v.source === 'permit' ? 'Permit' : v.source === 'monthly' ? 'Monthly' : 'Paid'
    // A lapsed session inside the internal grace period gets the same roster
    // pill as a live paid session. A separate word would restore the countdown.
    case 'grace': return 'Paid'
    case 'pending': return 'Pending approval'
    case 'expired': return 'Expired'
    case 'unverified': return 'Unconfirmed'
    case 'no_record': return 'No record'
    // Not "OK" and not "Exempt". The row is making no claim about this car at
    // all — the lot is shut, so there is nothing here to be right or wrong about.
    // An Inactive lot rides the same verdict and is told apart by its reason code:
    // the asphalt may be open, Parkify is just not taking payment on it.
    // An archived lot is the third: Parkify no longer operates there at all.
    case 'closed': return isLotArchived(v) ? 'No longer on Parkify' : isNotTakingPayment(v) ? 'Not taking payment' : 'Lot closed'
  }
}

/**
 * An ARCHIVED lot's row (docs/ZONE-ARCHIVE-SPEC.md §8.1): the same `closed`
 * verdict, told apart by `zone_archived`. Its wording never tells the officer
 * whether to enforce: the lot may now be run by someone else, and Parkify's
 * statement is only that it issues nothing there. Provisional until Austin
 * signs off the officer copy (Q3).
 */
export function isLotArchived(v: Pick<Vehicle, 'verdict' | 'verdictReason'>): boolean {
  return v.verdict === 'closed' && v.verdictReason?.code === 'zone_archived'
}

/**
 * An Inactive lot's withdrawn row: the server's ENFORCEMENT HOLD, which shares the
 * `closed` verdict with a closure (so older bundles and partners still read it as
 * "do not act") and differs only in its reason code. One definition, so the pill,
 * the card headline and the summary count cannot disagree about which it is.
 */
export function isNotTakingPayment(v: Pick<Vehicle, 'verdict' | 'verdictReason'>): boolean {
  return v.verdict === 'closed' && v.verdictReason?.code === 'zone_inactive'
}

/**
 * The second line of a list row.
 *
 * Not just `v.detail`: the server's detail for a lapsed session reads "expired
 * 23 min ago", which under an "EXPIRED" label renders as "EXPIRED / expired 23
 * min ago". The server keeps the self-describing string (it has no idea what
 * label sits above it); the row strips the redundant word.
 *
 * That stripping is why `verdictReason` exists and why it is checked FIRST. Re-deriving
 * the line from `minutesAgo` means anything the server put in `detail` that this
 * function doesn't recognize is discarded — and #1807 put the permit's access
 * schedule there. With `minutesAgo` null (an out-of-hours permit has no instant
 * it lapsed at) the row rendered the bare string " ago" while the server was
 * sending "ACME Corp · outside access hours (Mon–Fri 7 AM–6:30 PM)". A reason is
 * the one thing here that is rendered rather than re-derived.
 */
export function subLabelFor(v: Vehicle, timezone?: string | null, spoken = false): string {
  // Grace is the one exception to reason-first rendering. It is deliberately
  // indistinguishable from a live paid session on the roster, so even a future
  // server reason must not restore a countdown or a grace-specific phrase.
  if (v.verdict === 'grace') return ''
  // Outranks every case below. The server sets it exactly when the label sitting
  // above this line is not the whole truth about the car, so there is no verdict
  // for which the elapsed clock is the better line.
  //
  // This is also the whole of the `closed` row's second line, and the reason that
  // verdict needed no case of its own here: #1880 made the reason a field every
  // renderer of a negative row must read, so the closure's reason arrived on the
  // roster for free. Had the schedule still been travelling in `detail`, a closed
  // row would have re-asserted "expired 40 min ago" under a "Lot closed" label.
  if (v.verdictReason) return v.verdictReason.short
  if (v.verdict === 'expired') {
    // No elapsed time and no reason: fall through to the server's own words
    // rather than emitting " ago" around an empty string. `formatMinutes(null)`
    // returns '' by design, which makes every `${...} ago` template here a
    // latent blank line — this is the guard that stops it being a rendered bug.
    if (v.minutesAgo == null) return v.detail
    // The row says it the way the demo does, "Ended 37m ago". Only the expired
    // branch asserts an end: grace returned above, and unverified (below) stays a
    // bare elapsed time so it never claims the session ended.
    return spoken ? `${formatMinutes(v.minutesAgo)} ago` : `Ended ${rowDuration(v.minutesAgo)} ago`
  }
  if (v.verdict === 'authorized' && v.source === 'session') {
    if (v.minutesLeft === 0) return PAST_END
    const clock = expiryClock(v, timezone)
    const left = spoken ? formatMinutes(v.minutesLeft) : rowDuration(v.minutesLeft)
    return clock ? `${left} left · ${clock}` : `${left} left`
  }
  if (v.verdict === 'authorized' && v.source === 'permit') return withValidThrough(v)
  // `unverified` carries the server's "expired N min ago" detail, which would sit
  // under an "UNCONFIRMED" label and quietly re-assert the very thing the server
  // declined to say. Strip it to the bare elapsed time and let the label mean
  // what it says.
  if (v.verdict === 'unverified' && v.minutesAgo != null) return `${formatMinutes(v.minutesAgo)} ago`
  // The server writes a single pending request as "<tenant>, waiting <age>" so the
  // result card can say "at <tenant>, waiting <age>". The row separates the two
  // facts with the middle dot every other row detail uses, as the demo does. Row
  // only: the card sentence below keeps the server's comma.
  if (v.verdict === 'pending' && !spoken) {
    const m = PENDING_SINGLE.exec(v.detail)
    if (m) return `${m[1]} · waiting ${m[2]}`
  }
  return v.detail
}

const PENDING_SINGLE = /^(.+), waiting (.+)$/

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/**
 * "Dec 31" for a `YYYY-MM-DD` calendar date, with the year added when it is not
 * the current one ("Dec 31, 2027"). Built from the digits, never through `Date`
 * parsing, so the phone's timezone cannot move a calendar date a day. Null for
 * anything that is not a real date, so a bad value renders nothing instead of
 * "Valid through undefined NaN".
 */
export function formatCalendarDate(iso: string | null | undefined, now: Date = new Date()): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso ?? '')
  if (!m) return null
  const year = Number(m[1]); const month = Number(m[2]); const day = Number(m[3])
  if (month < 1 || month > 12 || day < 1 || day > 31) return null
  return year === now.getFullYear() ? `${MONTHS[month - 1]} ${day}` : `${MONTHS[month - 1]} ${day}, ${year}`
}

/**
 * The permit's own words plus "Valid through <date>", when the server sent a
 * date and the row has no hours clock. An hours-limited permit already carries
 * "until 6:30 PM", which is the nearer fact for an officer standing at the car,
 * so the date is not stacked on top of it.
 */
function withValidThrough(v: Vehicle): string {
  if (v.expiresAt) return v.detail
  const date = formatCalendarDate(v.validThrough)
  return date ? `${v.detail} · Valid through ${date}` : v.detail
}

/**
 * What a screen reader says for a roster row's button.
 *
 * A button's aria-label REPLACES its content as the accessible name. Labelling
 * the row "Check <plate>" therefore hid everything the officer decides on: the
 * verdict, the elapsed or remaining time, the STATED / ASSUMED / NO STATE mark and
 * the permit or monthly tag. It also broke label-in-name (WCAG 2.5.3), because the
 * visible "Expired" was not in the name.
 *
 * So the name is built from the same strings the row renders, in reading order,
 * leading with the plate. `subLabelFor` is reused rather than re-derived (with the
 * same lot timezone the row renders with), which keeps grace blank: no countdown
 * or re-check time can reach a screen reader that a sighted officer does not see.
 */
export function rowAccessibleName(v: Vehicle, timezone?: string | null): string {
  const authorized = v.verdict === 'authorized' || v.verdict === 'grace'
  const tagged = authorized && (v.source === 'permit' || v.source === 'monthly')
  const parts = [
    v.plate,
    plateStateMarkSpoken(v),
    tagged ? labelFor(v) : '',
    authorized ? 'Authorized' : labelFor(v),
    subLabelFor(v, timezone, true),
  ].filter(Boolean)
  return `${parts.join(', ')}. Open result`
}

/** The same for a near-miss button: plate, mark, verdict and the reason it was offered. */
export function candidateAccessibleName(c: Vehicle & { reason: string }): string {
  const parts = [c.plate, plateStateMarkSpoken(c), labelFor(c), c.reason].filter(Boolean)
  return `${parts.join(', ')}. This is the car`
}

/**
 * Keep a clock time on one line: "6:30 PM" and "7 AM" take a no-break space
 * between the digits and AM/PM, so a narrow row wraps before the time rather than
 * inside it and never leaves a lone "PM" on the next line. Display only: the
 * strings the server sends, and the accessible names built from them, are
 * unchanged.
 */
export function keepTimesTogether(text: string): string {
  return text.replace(/(\d) (AM|PM)\b/g, '$1\u00A0$2')
}

/**
 * A near-miss reason with its first letter capitalised. The server's reasons are
 * lowercase fragments ("a character missing", "I read as 1") written to be read
 * on their own; under a capitalised pill ("Expired") a lowercase-leading fragment
 * reads as the tail of a sentence. Wording is untouched, so "read as" still points
 * the officer's eye at one character.
 */
export function capitalizeReason(reason: string): string {
  return reason ? reason.charAt(0).toUpperCase() + reason.slice(1) : reason
}

/**
 * Splits a closed card's sentence so its closing "No session is expected here ..."
 * can sit on a quieter line of its own, as in the demo. Display only: the two parts
 * joined with a space are exactly the server's text, and a sentence that does not
 * contain the marker comes back whole with no note.
 */
export function splitClosedGuidance(text: string): { main: string; note: string | null } {
  const i = text.indexOf('No session is expected')
  if (i <= 0) return { main: text, note: null }
  return { main: text.slice(0, i).trimEnd(), note: text.slice(i) }
}

/** Full-sentence verdict for the single-plate result card. */
/**
 * A paid session's minutes left, re-derived from its end instant at `nowMs`.
 *
 * The snapshot's `minutesLeft` is frozen at the moment the server built it, so
 * on a roster that has been open for a while "12 min left" is a stale claim
 * about a clock that kept running. `expiresAt` is absolute, so the client can
 * age it without a request, with the server's own rounding (ceil, floored at 0).
 *
 * Colour and wording only. This never changes a verdict: a row whose end has
 * passed stays `authorized` here, because only the server may say grace or
 * expired. It reads "Past end time" and the stale banner offers the refresh.
 * Grace never reaches this, so no countdown can appear on it.
 */
export function withLiveMinutes(v: Vehicle, nowMs: number): Vehicle {
  if (v.verdict !== 'authorized' || v.source !== 'session' || !v.expiresAt) return v
  const end = Date.parse(v.expiresAt)
  if (Number.isNaN(end)) return v
  return { ...v, minutesLeft: Math.max(0, Math.ceil((end - nowMs) / 60_000)) }
}

const PAST_END = 'Past end time, refresh to confirm'

/** Demo parity (`ENFORCE_SOON_MIN`): a paid session this close to ending is worth a second look on a sweep. */
export const ENDING_SOON_MINUTES = 15

/**
 * True for an authorized paid session with at most ENDING_SOON_MINUTES left.
 * Colour only: the verdict stays Authorized, and grace never reaches this.
 */
export function endingSoon(v: Vehicle): boolean {
  return v.verdict === 'authorized' && v.source === 'session'
    && v.minutesLeft != null && v.minutesLeft <= ENDING_SOON_MINUTES
}

/**
 * A paid session's end as a clock time in the LOT's timezone ("4:22 PM"), or null
 * when we cannot say it correctly. Never falls back to the officer's own clock:
 * a phone in another zone would print a confident wrong time.
 */
export function expiryClock(v: Vehicle, timezone: string | null | undefined): string | null {
  if (v.verdict !== 'authorized' || v.source !== 'session' || !v.expiresAt || !timezone) return null
  const ms = Date.parse(v.expiresAt)
  if (Number.isNaN(ms)) return null
  try {
    return new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit', timeZone: timezone }).format(ms)
  } catch {
    return null
  }
}

/**
 * "Lapsed at 2:22 PM" for an expired paid session, in the LOT's timezone, or null
 * when we cannot say it correctly. The demo's card carries it under the elapsed
 * sentence; it is a past-tense fact about what already happened, so it adds no
 * re-check time and says nothing about grace (the demo's "past the 10-minute
 * grace" clause is the exposed internal figure and is NOT copied). A lapse that
 * was not today in the lot's timezone carries its date ("Lapsed Sep 29 at 11:40
 * PM"), so "Lapsed at 11:40 PM" is never read as this evening.
 *
 * Only a session whose end instant we hold: never grace, unverified, closed or a
 * permit, and never when a `verdictReason` replaces the elapsed-time sentence.
 */
export function lapseLine(v: Vehicle, timezone: string | null | undefined, nowMs: number = Date.now()): string | null {
  if (v.verdict !== 'expired' || v.source !== 'session' || !v.expiresAt || !timezone || v.verdictReason) return null
  const ms = Date.parse(v.expiresAt)
  if (Number.isNaN(ms)) return null
  try {
    const clock = new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit', timeZone: timezone }).format(ms)
    const day = (t: number) => new Intl.DateTimeFormat('en-CA', { year: 'numeric', month: '2-digit', day: '2-digit', timeZone: timezone }).format(t)
    if (day(ms) === day(nowMs)) return `Lapsed at ${clock}`
    const date = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', timeZone: timezone }).format(ms)
    return `Lapsed ${date} at ${clock}`
  } catch {
    return null
  }
}

export function headlineFor(v: Vehicle): string {
  switch (v.verdict) {
    case 'authorized': return 'Authorized'
    case 'grace': return 'Not enforceable'
    case 'pending': return 'Verify before acting'
    case 'expired': return 'Expired'
    case 'unverified': return 'Can’t confirm'
    case 'no_record': return 'No record'
    // The instruction is IN the headline, not left to the sentence below it.
    // This is the one card where the officer's correct action is to do nothing,
    // and "Lot closed" alone invites them to supply the rest themselves.
    // An archived lot's headline says what Parkify does and stops there (spec §4).
    case 'closed': return isLotArchived(v)
      ? 'No longer on Parkify'
      : isNotTakingPayment(v) ? 'Not taking payment: do not enforce' : 'Lot closed: do not enforce'
  }
}

/**
 * What "Unconfirmed" means when the server gives no reason of its own (a lot whose Parkify
 * records are not the whole picture, `applyCoverage`): Parkify holds no current record, and that
 * is neither proof the car paid nor proof it did not. Reports what Parkify holds and never says
 * what to do (see `PlateStateMark`).
 *
 * ⚠️ NO OTHER-SYSTEM WORDING. Owner decision 2026-09-30 removed the hybrid-coverage banner, and
 * the dev stub's `hybrid` scenario is pinned to carry none: Patrol does not tell an officer there
 * is another place a plate might have paid. So this says what OUR records can and cannot show,
 * and nothing about why (tests/verdict.test.ts pins the banned words). A plate whose Parkify
 * session just ended is the common case, and the line says so, so "I can see they paid" and
 * "Unconfirmed" stop reading as a contradiction.
 */
const UNVERIFIED_EXPLAINER =
  'Parkify holds no current record for this plate, so we can neither confirm nor rule out that it paid.'

const unverifiedFallback = (v: Pick<Vehicle, 'minutesAgo'>): string =>
  v.minutesAgo != null
    ? `The Parkify session for this plate ended ${formatMinutes(v.minutesAgo)} ago. ${UNVERIFIED_EXPLAINER}`
    : UNVERIFIED_EXPLAINER

/**
 * One plain definition per outcome WORD a roster row can show, for the "What do these words mean?"
 * panel. A definition has to be true of every row that wears the word, so each one is written
 * against what the server actually sends under that verdict (api/src/enforcement/authorization.ts):
 *
 *   expired     a Parkify session that ran out (`verdictForSession`), OR a live permit outside its
 *               access hours (`permit_out_of_hours`, ~L991). "The session ended" alone is false for
 *               the second, and that row is a car Parkify holds a live permit for.
 *   unverified  a no-record or expired row softened on a lot Parkify does not see whole
 *               (`applyCoverage`, which KEEPS the permit-out-of-hours reason), OR any negative row on
 *               a lot whose hours could not be read (`applyClosure`, reason "Lot hours unconfirmed").
 *               So "no current record" alone is false for two of its three shapes.
 *   closed      one verdict, three words (`labelFor`): Lot closed, Not taking payment (an Inactive
 *               lot's hold), No longer on Parkify (an archived lot).
 *
 * Reports what Parkify holds or does not hold; none of it says whether to cite. `grace` has no
 * entry on purpose: its row is deliberately the same as an authorized one (`isTappable`), and a
 * glossary that named it would reveal the countdown the words hide.
 */
const AUTHORIZED_MEANING = 'Parkify holds a current paid session, permit or monthly for this plate.'
const LOT_CLOSED_MEANING = 'The lot is closed right now, so Parkify makes no statement about cars in it.'
const NOT_TAKING_PAYMENT_MEANING =
  'Parkify is not taking payment at this lot right now, so it makes no statement about cars in it.'
const ARCHIVED_MEANING =
  'Parkify no longer runs this lot. It issues nothing there and makes no statement about cars in it.'

/** The words, in panel order. `labelFor` is asserted to print nothing outside this list (verdict.test.ts). */
const GLOSSARY_OUTCOMES: ReadonlyArray<{ term: string; meaning: string }> = [
  { term: 'Authorized', meaning: AUTHORIZED_MEANING },
  { term: 'Pending approval', meaning: 'A tenant registration for this plate is waiting for approval. It grants nothing yet.' },
  {
    term: 'Unconfirmed',
    meaning: 'Parkify cannot confirm this plate either way right now. Either it holds no current record for'
      + ' the plate, or what it holds depends on hours it cannot confirm (a permit’s access hours, or the'
      + ' lot’s own hours). The row says which when Parkify knows.',
  },
  {
    term: 'Expired',
    meaning: 'The Parkify session for this plate has ended, or the plate has a live permit that is outside'
      + ' its access hours right now. The row says which.',
  },
  { term: 'No record', meaning: 'Parkify holds no current payment, permit or monthly for this plate.' },
  { term: 'Lot closed', meaning: LOT_CLOSED_MEANING },
  { term: 'Not taking payment', meaning: NOT_TAKING_PAYMENT_MEANING },
  { term: 'No longer on Parkify', meaning: ARCHIVED_MEANING },
]

/**
 * A row's hover title (`title` on the pill): what THIS row means, not the word's whole definition.
 * When the server says why (`verdictReason`), that is the answer, verbatim: it is the one field
 * that tells an out-of-hours permit from an ended session, and a lot whose hours could not be read
 * from a plate with no record. Without a reason each verdict has exactly one server shape, so the
 * narrow sentence is true. `Record<Verdict, ...>`: a new verdict cannot ship without one.
 */
const ROW_MEANING_WITHOUT_REASON: Record<Verdict, string> = {
  authorized: AUTHORIZED_MEANING,
  grace: AUTHORIZED_MEANING,
  pending: 'A tenant registration for this plate is waiting for approval. It grants nothing yet.',
  expired: 'The Parkify session for this plate has ended.',
  unverified: UNVERIFIED_EXPLAINER,
  no_record: 'Parkify holds no current payment, permit or monthly for this plate.',
  closed: LOT_CLOSED_MEANING,
}

/** The meaning behind a row's outcome word (the pill's hover title). */
export function verdictMeaning(v: Pick<Vehicle, 'verdict' | 'verdictReason'>): string {
  return v.verdictReason?.long ?? ROW_MEANING_WITHOUT_REASON[v.verdict]
}

/** Every word a roster row can show, with what it means: outcome pills first, then the plate-state marks. */
export function glossaryEntries(): Array<{ term: string; meaning: string }> {
  const marks: PlateStateMark[] = ['stated', 'assumed', 'none']
  return [
    ...GLOSSARY_OUTCOMES,
    ...marks.map(mark => ({ term: plateStateMarkLabel(mark), meaning: plateStateMarkTitle(mark) })),
  ]
}

/** The server's detail for a plate with two or more pending requests. */
const MULTI_PENDING = /^\d+ pending requests/

/**
 * What an authorized permit or monthly card says the authorization IS. The
 * server's detail is only the holder's own label ("ACME Corp", "ACME Corp ·
 * until 6:30 PM", a plan name), which under a bare "Authorized" reads as an
 * unexplained string, and the PERMIT / MONTHLY chip lives on roster rows that
 * are hidden while a card is open. So the source is named in the sentence.
 *
 * When the detail already leads with the source word (the server's own default
 * labels are "Permit" and "Monthly") it is not repeated: "Active permit · until
 * 6:30 PM", never "Active permit · Permit · until 6:30 PM". A permit with no
 * hours clock also carries its calendar end date when the server sent one
 * (`validThrough`): "Active permit · ACME Corp · Valid through Dec 31".
 */
function authorizationSentence(v: Vehicle): string {
  if (v.source === 'permit') {
    const detail = withValidThrough(v).trim()
    if (!detail) return 'Active permit'
    if (/^permit\b/i.test(detail)) return detail.replace(/^permit/i, 'Active permit')
    return `Active permit · ${detail}`
  }
  if (v.source === 'monthly') {
    const detail = v.detail.trim()
    if (!detail || /^monthly$/i.test(detail)) return 'Monthly plan'
    if (/^monthly\b/i.test(detail)) return detail
    return `Monthly plan · ${detail}`
  }
  return v.detail
}

/**
 * The sentence under the headline. Every negative one ends in an instruction,
 * because "no record" on its own invites the officer to fill in the blank with
 * "so write the ticket".
 *
 * A `verdictReason` REPLACES the elapsed-time sentence rather than joining it, and it
 * suppresses the arrival-grace clause. Both for the same reason: those two
 * sentences are about a driver who was meant to buy parking and either ran out
 * or hasn't paid yet. Neither describes a permit holder whose arrangement is
 * live and simply doesn't cover 9pm — "Ran out 0 min ago. This lot allows 15 min
 * to pay after arriving." would be three false implications in one line. The
 * reason's own `long` already ends in what to do.
 */
export function guidanceFor(v: Vehicle, arrivalGraceMinutes: number | null, timezone?: string | null): string {
  switch (v.verdict) {
    case 'authorized':
      if (v.source !== 'session') return authorizationSentence(v)
      if (v.minutesLeft === 0) return `Paid parking. ${PAST_END}`
      {
        const clock = expiryClock(v, timezone)
        return clock
          ? `Paid parking, ${formatMinutes(v.minutesLeft)} left, until ${clock}`
          : `Paid parking, ${formatMinutes(v.minutesLeft)} left`
      }
    case 'grace':
      // Do not render server-authored reason prose here. The grace answer is a
      // privacy boundary: it may explain the past, but it may never acquire a
      // re-check time or "yet" through a future reason code.
      if (v.minutesAgo == null) return 'Inside the grace window. Not enforceable.'
      return `Session ended ${formatMinutes(v.minutesAgo)} ago, inside the grace window. Not enforceable.`
    case 'pending':
      // A plate with several open requests has no single tenant to name: the
      // server's detail is a count ("3 pending requests · oldest waiting 2
      // days"), and dropping it after "at" read as a broken sentence.
      if (MULTI_PENDING.test(v.detail)) {
        return `Registration pending approval: ${v.detail.replace(' · oldest', ', oldest')}. Not authorized and not a clean violation, confirm with the tenant before acting.`
      }
      return `Registration pending approval at ${v.detail}. Not authorized and not a clean violation, confirm with the tenant before acting.`
    case 'expired':
      if (v.verdictReason) return v.verdictReason.long
      // Guard, same class as subLabelFor's: without a reason AND without an
      // elapsed time there is no honest "ran out" sentence to write, so say the
      // one thing we do know instead of "Ran out  ago".
      if (v.minutesAgo == null) {
        return arrivalGraceMinutes
          ? `We hold no current authorization for this plate. This lot allows ${arrivalGraceMinutes} min to pay after arriving.`
          : 'We hold no current authorization for this plate.'
      }
      return arrivalGraceMinutes
        ? `Ran out ${formatMinutes(v.minutesAgo)} ago. This lot allows ${arrivalGraceMinutes} min to pay after arriving.`
        : `Ran out ${formatMinutes(v.minutesAgo)} ago`
    case 'unverified':
      return v.verdictReason ? v.verdictReason.long : unverifiedFallback(v)
    case 'no_record':
      return arrivalGraceMinutes
        ? `No Parkify payment, permit or monthly for this plate. This lot allows ${arrivalGraceMinutes} min to pay after arriving.`
        : 'No Parkify payment, permit or monthly for this plate.'
    case 'closed':
      // The server's whole sentence, verbatim: the driver-facing reason, the
      // reopen time worded in the LOT's timezone, and why the absence of a
      // session means nothing here. The arrival-grace clause is suppressed for
      // the same reason it is on a permit reason row — it is advice for someone
      // who was meant to buy parking, and nobody could buy any of this.
      //
      // `?? detail` is the shape-guard, not an expected path: the server always
      // sends a reason on a `closed` row (applyClosure composes it in the same
      // breath as the verdict). If that ever stops being true this degrades to
      // the server's short line instead of rendering an empty card.
      return v.verdictReason?.long ?? v.detail
  }
}

/**
 * The card's sentence as the demo lays it out: the figure the officer acts on in
 * bold, and the second fact on a quieter grey line of its own, instead of one flat
 * paragraph. DISPLAY ONLY. `guidanceFor` stays the plain-text source of truth (the
 * screen-reader announcement and every wording test read it); this only decides
 * where the weight and the line break go, and what it returns never adds a word the
 * verdict did not already carry:
 *   - a paid session: "Paid parking, **1h 30m left**" over "Expires 4:22 PM" (the
 *     same clock `guidanceFor` writes as "until 4:22 PM")
 *   - expired: "Ran out **23 min ago**. ..." (the lapse time is its own grey line,
 *     see `lapseLine`)
 *   - grace: "Session ended **4 min ago**, inside the grace window." The figure is a
 *     past-tense fact; it gains no countdown and no re-check time
 *   - pending: "Registration **pending approval** at **Tenant**, ..." over the grey
 *     "Not authorized and not a clean violation, confirm with the tenant before
 *     acting."
 * `bold` lists exact substrings of `main`; a substring that is not there is simply
 * not bolded, so a wording change can never throw or drop text.
 */
export interface GuidanceParts { main: string; bold: string[]; second: string | null }

const PENDING_SECOND = 'Not authorized and not a clean violation'

export function guidanceParts(v: Vehicle, arrivalGraceMinutes: number | null, timezone?: string | null): GuidanceParts {
  const text = guidanceFor(v, arrivalGraceMinutes, timezone)
  switch (v.verdict) {
    case 'authorized': {
      if (v.source !== 'session' || v.minutesLeft === 0) return { main: text, bold: [], second: null }
      const left = `${formatMinutes(v.minutesLeft)} left`
      const clock = expiryClock(v, timezone)
      return { main: `Paid parking, ${left}`, bold: [left], second: clock ? `Expires ${clock}` : null }
    }
    case 'grace':
      return { main: text, bold: v.minutesAgo == null ? [] : [`${formatMinutes(v.minutesAgo)} ago`], second: null }
    case 'expired':
      return {
        main: text,
        bold: v.verdictReason || v.minutesAgo == null ? [] : [`${formatMinutes(v.minutesAgo)} ago`],
        second: null,
      }
    case 'pending': {
      const i = text.indexOf(PENDING_SECOND)
      const main = i > 0 ? text.slice(0, i).trimEnd() : text
      const second = i > 0 ? text.slice(i) : null
      // The detail reads "Basket Case Market, waiting 2 days": only the tenant is the
      // bold name, as in the demo, not the wait that follows it.
      const name = v.detail.split(/,\s*waiting\b/)[0].trim()
      const tenant = !MULTI_PENDING.test(v.detail) && name ? [name] : []
      return { main, bold: ['pending approval', ...tenant], second }
    }
    default:
      return { main: text, bold: [], second: null }
  }
}

/**
 * Which zone a facility answer came from, in the card's words: "Paid in GA634"
 * when a session authorizes, "Permit valid in GA634 · GA644" for a standing
 * grant, and the verdict word with "in" for everything else.
 */
export function facilityZoneLine(v: Vehicle): string {
  const where = (v.zoneNames ?? [v.zoneName ?? '']).filter(Boolean).join(' · ')
  if (v.verdict === 'authorized') {
    if (v.source === 'session') return `Paid in ${where}`
    if (v.source === 'permit') return `Permit valid in ${where}`
    if (v.source === 'monthly') return `Monthly valid in ${where}`
    return `Authorized in ${where}`
  }
  return `${labelFor(v)} in ${where}`
}
